import React, { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { api } from "@/lib/api";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { breadcrumbSchema } from "@/components/JsonLd";
import { useSiteContent } from "@/context/SiteContentContext";

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const c = useSiteContent("testimonials");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/testimonials");
        setTestimonials(data);
      } catch (_) {}
    })();
  }, []);

  return (
    <PageLayout>
      <Seo
        title="Customer Reviews — 4.9 out of 5 · 520+ travelers"
        description="Verified reviews from Tour Trips India Holidays travelers. 4.9/5 on TripAdvisor, Google and direct guest letters — covering Golden Triangle, Spiritual India, Rajasthan, Nepal, Dubai and Thailand tours."
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Reviews", path: "/testimonials" },
        ])}
      />
      <PageHero
        eyebrow={c.hero_eyebrow}
        title={c.hero_title}
        subtitle={c.hero_subtitle}
        image={c.hero_image}
      />

      <section className="py-24 md:py-32" data-testid="testimonials-page">
        <div className="container-tti">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">Reviews</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                4.9 out of 5,
                <br />
                <span className="italic text-deepred">520+ reviews.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-foreground/75 text-lg font-light leading-relaxed">
                Verified on TripAdvisor, Google Reviews and our own inbox. Below is a selection of letters that made
                our day.
              </p>
            </div>
          </div>

          {testimonials.length === 0 ? (
            <div className="text-center py-12 text-foreground/60">Loading reviews…</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  data-testid={`testimonial-card-${t.id}`}
                  className="bg-white border border-cream-200 rounded-sm p-8 md:p-10 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-1 mb-5">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-saffron text-saffron" />
                    ))}
                  </div>
                  <Quote className="w-8 h-8 text-saffron mb-4 opacity-60" />
                  <p className="text-foreground/85 text-[17px] font-light leading-relaxed mb-8">{t.text}</p>
                  <div className="flex items-center gap-4 pt-5 border-t border-cream-200">
                    <img src={t.avatar_url} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <div className="font-serif text-xl">{t.name}</div>
                      <div className="text-xs uppercase tracking-[0.2em] text-saffron mt-1">{t.location}</div>
                      <div className="text-xs text-foreground/60 mt-0.5">{t.tour_name}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}
